import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTheme } from '@mui/material';
import { useGetCompanyTheme } from 'ogp/hooks';
import { CompanyTheme } from 'shared/contexts/theme-provider';
import type { UseSettingsEditThemeFormResult } from './use-settings-edit-theme-form.types';
import type { SettingEditThemeFormData } from '../setting-edit-theme/setting-edit-theme-form.types';

const useSettingsEditThemeForm = (): UseSettingsEditThemeFormResult => {
  const theme = useTheme();
  const themeQuery = useGetCompanyTheme();

  const originalTheme = useRef<CompanyTheme>({
    primaryColor: theme.palette.primary.main,
    secondaryColor: theme.palette.secondary.main,
    primaryAccent: theme.palette.lwPrimaryAccentLight[100],
    secondaryAccent: theme.palette.lwSecondaryAccentLight[100],
    logo: themeQuery.data?.logo ?? undefined,
  });

  const formMethods = useForm<SettingEditThemeFormData>({
    defaultValues: {
      colors: {
        primaryColor: theme.palette.primary.main,
        secondaryColor: theme.palette.secondary.main,
        primaryAccent: theme.palette.lwPrimaryAccentLight[100],
        secondaryAccent: theme.palette.lwSecondaryAccentLight[100],
      },
      logo: {
        portalLogoWide: themeQuery.data?.logo?.portalLogoWideUrl ?? null,
        portalLogoSquare: themeQuery.data?.logo?.portalLogoSquareUrl ?? null,
      },
    },
  });

  const syncedThemeDataRef = useRef<CompanyTheme | null | undefined>();
  useEffect(() => {
    if (!themeQuery.isSuccess || !themeQuery.data) {
      return;
    }

    if (syncedThemeDataRef.current === themeQuery.data) {
      return;
    }

    const currentWide = formMethods.getValues('logo.portalLogoWide');
    const currentSquare = formMethods.getValues('logo.portalLogoSquare');
    if (currentWide instanceof File || currentSquare instanceof File) {
      syncedThemeDataRef.current = themeQuery.data;
      return;
    }

    syncedThemeDataRef.current = themeQuery.data;
    const wideUrl = themeQuery.data.logo?.portalLogoWideUrl ?? null;
    const squareUrl = themeQuery.data.logo?.portalLogoSquareUrl ?? null;

    formMethods.reset((values) => ({
      ...values,
      logo: {
        portalLogoWide: wideUrl,
        portalLogoSquare: squareUrl,
      },
    }));
    originalTheme.current = {
      ...originalTheme.current,
      logo: themeQuery.data.logo ?? undefined,
    };
  }, [themeQuery.isSuccess, themeQuery.data]);

  return { formMethods, originalThemeRef: originalTheme };
};

export { useSettingsEditThemeForm };
